// frontend/src/tabs/TabsProvider.jsx
//
// <TabsProvider> wraps the protected route subtree and owns the tab state;
// the contexts it fills live in tabsContexts.js.
//
// The reducer is the single point that mutates tab state. Policy — cap
// enforcement, focus-or-create dedup, Home staying first on reorder — is
// useTabs'. Home never closing and "active tab follows close" are here, so no
// caller can skip them.

import { useEffect, useMemo, useReducer } from 'react';
import {
  HOME_TAB_ID,
  loadTabsState,
  saveTabsState,
  seedHomeTab,
  clearTabSlots,
} from './storage';
import { markClean } from './useDirtyTabs';
import { TabsStateContext, TabsDispatchContext } from './tabsContexts';

function generateTabId() {
  const rand = Math.random().toString(36).slice(2, 10);
  return `tab_${rand}`;
}

// Policy lives in useTabs (cap enforcement, dedup-vs-create, id
// validation). The reducer is a pure data transformer — no validation,
// no policy branches. Reducer throws are dangerous because React runs
// reducers during the next render: a throw inside a case escapes to the
// ErrorBoundary instead of reaching the caller's try/catch around
// dispatch().
function reducer(state, action) {
  switch (action.type) {
    case 'OPEN': {
      // Always creates a new tab. The hook decides between OPEN (create)
      // and FOCUS (dedup); cap enforcement is the hook's job.
      // No title: the strip names a tab from routeRegistry by its path, so a
      // stored tab never carries a copy of the page's name.
      const tab = {
        id: generateTabId(),
        path: action.path,
        openedAt: Date.now(),
      };
      return { tabs: [...state.tabs, tab], activeId: tab.id };
    }

    case 'CLOSE': {
      const { id } = action;
      if (id === HOME_TAB_ID) return state; // home is non-closeable

      const idx = state.tabs.findIndex((t) => t.id === id);
      if (idx === -1) return state;

      const tabs = state.tabs.filter((t) => t.id !== id);

      // sweep per-tab filter storage + dirty registry so a long browser
      // session doesn't accumulate orphan keys/entries.
      clearTabSlots(id);
      markClean(id);

      // if we closed the active tab, focus the neighbor (prefer the one to
      // the left, fall back to home). Matches browser tab close semantics.
      let activeId = state.activeId;
      if (state.activeId === id) {
        const neighbor = state.tabs[idx - 1] || state.tabs[idx + 1];
        activeId = neighbor ? neighbor.id : HOME_TAB_ID;
      }
      return { tabs, activeId };
    }

    case 'FOCUS':
      // id validity is the hook's job (see useTabs.focus()).
      return { ...state, activeId: action.id };

    case 'CLOSE_ALL_EXCEPT_HOME': {
      // Side effects (clearTabSlots, markClean, sessionStorage write) live
      // in useTabs.closeAllExceptHome() so the storage write lands
      // synchronously before the caller replaces the document (the store
      // switch does, at the path that call returns). Reducer stays pure.
      const home = state.tabs.find((t) => t.id === HOME_TAB_ID) || seedHomeTab();
      return { tabs: [home], activeId: HOME_TAB_ID };
    }

    case 'SET_TITLE': {
      const { id, title } = action;
      const tabs = state.tabs.map((t) =>
        t.id === id ? { ...t, customTitle: title } : t,
      );
      return { ...state, tabs };
    }

    case 'REORDER': {
      // Pure array move: pull the tab at fromId out and re-insert it at
      // toId's slot. The Home-stays-pinned guard is policy and lives in the
      // hook (useTabs.reorderTabs) — this case is a plain data transform.
      // Unknown ids collapse to a no-op rather than corrupting the array.
      const { fromId, toId } = action;
      const from = state.tabs.findIndex((t) => t.id === fromId);
      const to = state.tabs.findIndex((t) => t.id === toId);
      if (from === -1 || to === -1 || from === to) return state;
      const tabs = [...state.tabs];
      const [moved] = tabs.splice(from, 1);
      tabs.splice(to, 0, moved);
      return { ...state, tabs };
    }

    default:
      // Don't throw — same render-time-throw hazard as FOCUS/OPEN. An
      // unknown action type is a dev-time bug (typo, refactor mismatch);
      // surface it loudly in the console but keep the UI alive.
      console.error('tabs: unknown action', action.type);
      return state;
  }
}

export function TabsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadTabsState);

  useEffect(() => {
    saveTabsState(state);
  }, [state]);

  const value = useMemo(() => ({ ...state, dispatch }), [state]);

  return (
    <TabsStateContext.Provider value={value}>
      <TabsDispatchContext.Provider value={dispatch}>
        {children}
      </TabsDispatchContext.Provider>
    </TabsStateContext.Provider>
  );
}
